'use client';

import posthog from 'posthog-js';

/**
 * Bridges the shared `useAnalytics().track()` calls into PostHog.
 *
 * The call sites keep their Vercel Analytics names (Title Case) and payloads — renaming them would
 * break existing Vercel history and touch six call sites across two apps. Instead, the events that
 * also exist in the mobile app are renamed here, to the schema the app has been sending for a long
 * time, so web and app land on the same event and can be compared inside one PostHog experiment.
 *
 * Events not listed here pass through unchanged (Title Case), which is why PostHog holds a mix of
 * naming styles: only cross-platform events are worth the indirection of a mapping.
 */
const POSTHOG_EVENT_NAMES: Record<string, string> = {
    'Ticket Button Clicked': 'showing_ticket_clicked'
};

/**
 * Property schema per mapped event, `web key` → `app key`. Mapped events send ONLY these keys —
 * anything else the web call sites pass (legacy Vercel-only properties) is dropped rather than
 * leaking a second, camelCase spelling of the same data into PostHog.
 *
 * `message_id` from the app's schema has no web equivalent (no push notifications), so it is
 * simply absent here.
 */
const POSTHOG_EVENT_PROPERTIES: Record<string, Record<string, string>> = {
    'Ticket Button Clicked': {
        showingId: 'showing_id',
        showingName: 'showing_name',
        contentId: 'content_id',
        movieId: 'movie_id',
        cinemaId: 'cinema_id',
        cinemaName: 'cinema_name',
        isInternalBooking: 'is_internal_booking',
        hoursUntilShowing: 'hours_until_showing',
        source: 'source'
    }
};

/**
 * Ids reach us as numbers from most call sites but as strings from the ones that go through
 * `AnalyticsProvider`'s `overallData` (Vercel Analytics only accepts primitives, so they are
 * stringified there). The app sends numbers, and a property that is sometimes "123" and sometimes
 * 123 cannot be grouped or compared across platforms — so normalize the id fields back to numbers.
 */
const NUMERIC_PROPERTIES = new Set(['showing_id', 'content_id', 'movie_id', 'cinema_id']);

function normalizeValue(key: string, value: unknown): unknown {
    if (NUMERIC_PROPERTIES.has(key) && typeof value === 'string' && /^\d+$/.test(value)) {
        return Number(value);
    }
    return value;
}

function mapProperties(event: string, data: Record<string, unknown>): Record<string, unknown> {
    const schema = POSTHOG_EVENT_PROPERTIES[event];
    if (!schema) return data;

    const mapped: Record<string, unknown> = {};
    for (const [webKey, appKey] of Object.entries(schema)) {
        if (data[webKey] !== undefined) {
            mapped[appKey] = normalizeValue(appKey, data[webKey]);
        }
    }
    return mapped;
}

/**
 * Tracker for `AnalyticsProvider`. Uses the posthog-js singleton that `PostHogProvider` initializes;
 * while PostHog is disabled (no key configured) `__loaded` stays false and this is a no-op, so
 * Vercel Analytics keeps working on its own.
 */
export function posthogTracker(event: string, data: Record<string, unknown> = {}): void {
    if (!posthog.__loaded) return;

    posthog.capture(POSTHOG_EVENT_NAMES[event] ?? event, mapProperties(event, data));
}
